import React from 'react';
import { Card } from '@/components/ui/cards/Card';
import { Text } from '@/components/ui/text/Text';
import { useTranslation } from '@/i18n';
import { colors } from '@/assets/styles/colors';
import type { DashboardWeekDay, DayStatus } from '@/api/domains/attendance';


interface WeeklyAttendanceCardProps {
  days: DashboardWeekDay[];
  workedHours: string;
  totalHours: string;
  'data-testid'?: string;
}

const DAY_STATUS_COLOR: Record<DayStatus, string> = {
  complete: colors.feedback.success300,
  overtime: colors.important.main,
  late: colors.feedback.alert300,
  absent: colors.feedback.error300,
  pending: colors.grays.neutral66,
};

export const WeeklyAttendanceCard: React.FC<WeeklyAttendanceCardProps> = ({
  days,
  workedHours,
  totalHours,
  'data-testid': dataTestId,
}) => {
  const { t: translate } = useTranslation();

  const legendItems = [
    { color: colors.feedback.success300, label: translate('attendance:dashboard.weeklyCard.legend.complete') },
    { color: colors.feedback.alert300, label: translate('attendance:dashboard.weeklyCard.legend.late') },
    { color: colors.important.main, label: translate('attendance:dashboard.weeklyCard.legend.overtime') },
    { color: colors.feedback.error300, label: translate('attendance:dashboard.weeklyCard.legend.absent') },
  ];

  return (
    <Card elevation={2} borderRadius="16px" padding="24px" data-testid={dataTestId}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
        <Text
          variant="body-medium"
          fontFamily="montserrat"
          color={colors.grays.neutral66}
          style={{ fontWeight: 600 }}
          data-testid={`${dataTestId}-title`}
        >
          {translate('attendance:dashboard.weeklyCard.title')}
        </Text>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
          {days.map((day) => (
            <div
              key={day.dayIndex}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}
              data-testid={`${dataTestId}-day-${day.dayIndex}`}
            >
              <Text
                variant="body-medium"
                fontFamily="montserrat"
                color={colors.grays.neutral66}
                textAlign="center"
                style={{ fontSize: 16, width: 66 }}
              >
                {translate(`attendance:dashboard.weeklyCard.days.${day.dayIndex}`)}
              </Text>
              <div
                style={{
                  width: 69,
                  height: 46,
                  borderRadius: 16,
                  border: `1px solid ${DAY_STATUS_COLOR[day.status]}`,
                  backgroundColor: 'rgba(217, 217, 217, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Text
                  variant="body-medium"
                  fontFamily="montserrat"
                  color={colors.grays.neutral66}
                  textAlign="center"
                  style={{ fontSize: 16 }}
                >
                  {day.dayNumber}
                </Text>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text
            variant="body-medium"
            fontFamily="montserrat"
            color={colors.grays.neutral66}
            data-testid={`${dataTestId}-total-label`}
          >
            {translate('attendance:dashboard.weeklyCard.totalLabel')}
          </Text>
          <Text
            variant="body-medium"
            fontFamily="montserrat"
            color={colors.grays.neutral66}
            style={{ fontWeight: 500 }}
            data-testid={`${dataTestId}-total-value`}
          >
            {`${workedHours}/${totalHours} ${translate('attendance:dashboard.weeklyCard.hoursUnit')}`}
          </Text>
        </div>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 'auto' }}>
          {legendItems.map((item) => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  backgroundColor: item.color,
                  flexShrink: 0,
                }}
              />
              <Text variant="small-regular" color={colors.grays.neutral66}>
                {item.label}
              </Text>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};
